import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { User, UserStore } from '../../state/user.repository';
import { ParticipantSettingsStore } from '../../state/participant-settings.repository';
import { calcSpatialVolume } from './helpers/spatial-audio';

@Component({
  selector: 'andor-spatial',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './spatial.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './spatial.scss',
})
export class Spatial implements OnInit {
  @ViewChild('gridSvg', { static: true }) svgRef!: ElementRef<SVGSVGElement>;

  users = signal<User[]>([]);

  gridMin = -10;
  gridMax = 10;
  height = 600;
  width = 600;
  cellSize = 0;

  isDragging = false;
  selectedItem: User | null = null;
  dragOffset = { x: 0, y: 0 };

  get gridLines(): number[] {
    const lines = [];
    for (let i = this.gridMin; i <= this.gridMax; i++) {
      lines.push(i);
    }
    return lines;
  }

  constructor(
    private _usersStore: UserStore,
    private _participentSettingsStore: ParticipantSettingsStore
  ) {
    this._usersStore.userListUsers$.pipe().subscribe((users) => {
      this.users.set(users);
    });
  }

  ngOnInit() {
    this.cellSize = this.width / (this.gridMax - this.gridMin);
  }

  toSvgX(x: number): number {
    return (x - this.gridMin) * this.cellSize;
  }

  toSvgY(y: number): number {
    return this.height - (y - this.gridMin) * this.cellSize;
  }

  fromSvgX(px: number): number {
    return px / this.cellSize + this.gridMin;
  }

  fromSvgY(py: number): number {
    return (this.height - py) / this.cellSize + this.gridMin;
  }

  onMouseDown(event: MouseEvent, item: User) {
    event.stopPropagation();

    this.selectedItem = item;
    this.isDragging = true;

    const rect = this.svgRef.nativeElement.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    const centerX = this.toSvgX(item.coordinates.x);
    const centerY = this.toSvgY(item.coordinates.y);

    this.dragOffset.x = offsetX - centerX;
    this.dragOffset.y = offsetY - centerY;
  }

  @HostListener('window:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (!this.isDragging || !this.selectedItem) return;

    const rect = this.svgRef.nativeElement.getBoundingClientRect();
    const svgX = event.clientX - rect.left - this.dragOffset.x;
    const svgY = event.clientY - rect.top - this.dragOffset.y;

    let x = Math.min(this.gridMax, Math.max(this.gridMin, this.fromSvgX(svgX)));
    let y = Math.min(this.gridMax, Math.max(this.gridMin, this.fromSvgY(svgY)));

    this.selectedItem.coordinates = { x, y, z: 0 };

    const volume = calcSpatialVolume(
      this.gridMax,
      { x: 0, y: 0, z: 0 },
      this.selectedItem.coordinates,
      this._participentSettingsStore.get(this.selectedItem.id)?.volume ?? 1
    );

    this.selectedItem.audioOutput?.setVolume(volume);
  }

  @HostListener('window:mouseup')
  onMouseUp() {
    this.selectedItem = null;
    this.isDragging = false;
  }
}
